import SparkMD5 from 'spark-md5';
import Service from '../Service';
import LocalStorage from '../LocalStorage';
import Constants from '../../submodules/logictry_config/constants';
import VersionChecker from '../VersionChecker';
import { debugLog } from '../../utils/debugLog';
const storage = new LocalStorage('logictry.server');

let _internetDisabled = false;
const _currentRequests = {};
const _rateLimitRequests = {};
let _tokens = {};

const API_KEY_TOKEN = 'api-key-token';
const CONTENT_TOKEN_PREFIX = 'content-token-';

function isLogictryHost(uri) {
  if (typeof uri !== 'string') return false;
  try {
    const { hostname } = new URL(uri, window.location.origin);
    return hostname === 'logictry.com' || hostname.endsWith('.logictry.com');
  } catch {
    return false;
  }
}

class Server extends Service {
  get internetDisabled() {
    return _internetDisabled;
  }
  async checkAuth(clearContext) {
    return this.__getRequest(clearContext ? '/auth?clearContext=1' : '/auth');
  }
  registerAccount(body) {
    const register = { action: 'createAccount', ...body };
    return this.__postRequest('/auth', register);
  }
  getApp(body) {
    return this.__postRequest('/auth', { action: 'getApp', ...body });
  }
  clearAccount() {
    return this.__postRequest('/auth', { action: 'clearAccount' });
  }
  verifyCode(email, code) {
    return this.__patchRequest('/auth', { action: 'verifyCode', email, code });
  }
  // enableMFA() {
  //   return this.__patchRequest('/auth', { action: 'enableMFA' });
  // }
  // verifyMFA(token) {
  //   return this.__patchRequest('/auth', { action: 'verifyMFA', token });
  // }
  // disableMFA() {
  //   return this.__patchRequest('/auth', { action: 'disableMFA' });
  // }
  loginAccount(body) {
    return this.__postRequest('/auth', body);
  }
  resetPassword(email) {
    return this.__patchRequest('/auth', { action: 'resetPassword', email });
  }
  clearSessionStorage() {
    storage.state = {};
    storage.saveState();
  }

  getWebsocketToken() {
    return storage.state?.websocketToken;
  }

  getSessionTokens() {
    const { accessToken, sessionToken } = storage.state || {};
    if (accessToken && sessionToken) return { accessToken, sessionToken };
    return null;
  }

  logout() {
    this.clearSessionStorage();
    const update = { action: 'logout' };
    if (Constants.isIphone) update.isIphone = true;
    return this.__patchRequest('/auth', update);
  }
  updatePassword(password) {
    return this.__patchRequest('/auth', { password });
  }
  getProfile() {
    return this.__getRequest('/users');
  }
  updateProfile(p) {
    return this.__patchRequest('/users', p);
  }
  getCustomerInfo(a) {
    return this.__getRequest(`/users?email=${a}`);
  }
  setDeviceId(device) {
    return this.__patchRequest('/users', { action: 'setDeviceInfo', device });
  }
  getTeams() {
    return this.__getRequest('/teams');
  }
  getTeamById(id) {
    return this.__getRequest(`/teams/${id}`);
  }
  searchBusinesses(query) {
    return this.__getRequest(query);
  }
  postError(error) {
    return this.__postRequest('/errors', { error });
  }
  postObservabilityEvent(body) {
    return this.__postRequest('/errors', body);
  }
  listErrors(query = {}) {
    const qs = new URLSearchParams();
    Object.keys(query).forEach((k) => {
      if (query[k] != null && query[k] !== '') qs.set(k, query[k]);
    });
    const q = qs.toString();
    return this.__getRequest(`/errors${q ? `?${q}` : ''}`);
  }
  getError(id) {
    return this.__getRequest(`/errors/${id}`);
  }
  getOccurrences(id, query = {}) {
    const qs = new URLSearchParams();
    Object.keys(query).forEach((k) => {
      if (query[k] != null && query[k] !== '') qs.set(k, query[k]);
    });
    const q = qs.toString();
    return this.__getRequest(`/errors/${id}/occurrences${q ? `?${q}` : ''}`);
  }
  updateError(id, body) {
    return this.__patchRequest(`/errors/${id}`, body);
  }
  bulkResolveErrors(filter, body = {}) {
    return this.__postRequest('/errors/bulk-resolve', { filter, ...body });
  }

  async __getRequest(url, _returnFullResponse, _contentTokenIds) {
    const response = await this.__request(
      {
        uri: `${Constants.logictryOrigin}/v1${url}`,
        method: 'GET',
        json: true,
      },
      _contentTokenIds,
    );
    if (_returnFullResponse) return response;
    return response && response.body;
  }
  async __postRequest(url, body, _returnFullResponse) {
    const response = await this.__request({
      uri: `${Constants.logictryOrigin}/v1${url}`,
      method: 'POST',
      json: true,
      body,
    });
    if (_returnFullResponse) return response;
    return response ? response.body : null;
  }
  async __patchRequest(url, body, _returnFullResponse) {
    const response = await this.__request({
      uri: `${Constants.logictryOrigin}/v1${url}`,
      method: 'PATCH',
      json: true,
      body,
    });
    if (_returnFullResponse) return response;
    return response && response.body;
  }
  async getContentType(url) {
    const response = await this.__request({
      uri: `${Constants.logictryOrigin}/links?contentTypeUrl=${encodeURIComponent(url)}`,
      method: 'GET',
      json: true,
    });
    return response && response.body && response.body.contentType;
  }
  async __deleteRequest(url, body, _returnFullResponse) {
    const response = await this.__request({
      uri: `${Constants.logictryOrigin}/v1${url}`,
      method: 'DELETE',
      json: true,
      body: body || {},
    });
    if (_returnFullResponse) return response;
    return response && response.body;
  }
  __request(options, contentTokenIds) {
    const requestSignature = SparkMD5.hash(`${options.method} ${options.uri} ${JSON.stringify(options.body)}`);
    // A duplicate of an in-flight request gets null, not the shared promise. Caches and
    // pagination query from render and re-query on every state update. Handing each
    // duplicate the promise attaches a callback per render to every pending request, and
    // each resolution then fans out into more renders and more attachments until the main
    // thread never leaves the microtask queue.
    if (_currentRequests[requestSignature]) return null;

    // Rate limit in 200 miliseconsecond intervals
    const timeout = setTimeout(() => {
      delete _rateLimitRequests[requestSignature];
    }, 200);
    if (_rateLimitRequests[requestSignature]) {
      const delta = Date.now() - _rateLimitRequests[requestSignature].lastTry;
      clearTimeout(_rateLimitRequests[requestSignature].timeout);
      _rateLimitRequests[requestSignature].timeout = timeout;

      // Allow retry every second, for 3 retries
      if (delta < 1000 || _rateLimitRequests[requestSignature].retryCount > 3) {
        if (_rateLimitRequests[requestSignature].retryCount === 4) {
          const rateLimitError = new Error(
            `Rate Limit Reached: ${options.method} ${options.uri} ${JSON.stringify(options.body)}`,
          );
          setTimeout(() => {
            throw rateLimitError;
          });
        }
        return null;
      }
      _rateLimitRequests[requestSignature].retryCount += 1;
      _rateLimitRequests[requestSignature].lastTry = Date.now();
    } else {
      _rateLimitRequests[requestSignature] = {
        retryCount: 0,
        lastTry: Date.now(),
        timeout,
      };
    }

    let resolve;
    const promise = new Promise((_resolve) => {
      resolve = _resolve;
    });
    _currentRequests[requestSignature] = resolve;

    // Include the csrf token
    options.headers = options.headers || {};
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    if (csrfToken) options.headers['csrf-token'] = csrfToken.content;

    // Add API Key Token
    if (_tokens[API_KEY_TOKEN]) options.headers[API_KEY_TOKEN] = _tokens[API_KEY_TOKEN];

    // Add Content Token(s)
    if (contentTokenIds) {
      if (Array.isArray(contentTokenIds)) {
        contentTokenIds.forEach((_id) => {
          const contentToken = `${CONTENT_TOKEN_PREFIX}${_id}`;
          if (_tokens[contentToken]) options.headers[contentToken] = _tokens[contentToken];
        });
      } else {
        const contentToken = `${CONTENT_TOKEN_PREFIX}${contentTokenIds}`;
        if (_tokens[contentToken]) options.headers[contentToken] = _tokens[contentToken];
      }
    }

    if (Constants.isIphone) {
      options.headers['platform'] = 'ios';
      const session = this.getSessionTokens();
      if (session) {
        options.headers['access-token'] = session.accessToken;
        options.headers['session-token'] = session.sessionToken;
      }
    }

    const finish = (result) => {
      const currentRequest = _currentRequests[requestSignature];
      if (currentRequest) {
        currentRequest(result);
        delete _currentRequests[requestSignature];
      }
    };

    const handleResponse = async (r) => {
      try {
        const isLogictry = options.uri.startsWith(Constants.logictryOrigin);
        if (isLogictry) {
          const apiVersion = r.getResponseHeader('api-version');
          VersionChecker.updateVersion(apiVersion);
        }
      } catch {
        //
      }

      if (r.statusCode === 500) {
        const body = (r.body && typeof r.body === 'object' && r.body.error)
          ? r.body
          : { error: 'INTERNAL_SERVER_ERROR' };
        finish({ body, status: r.statusCode });
      } else if (r.statusCode === 401) {
        finish({ body: r.body, status: r.statusCode });
        _tokens = {};
        this.clearSessionStorage();
        this.onLoggedOutStatusCode();
      } else {
        try {
          let body;
          if (r.body?.websocketToken || r.body?.accessToken && r.body?.sessionToken) {
            const nextState = { ...storage.state };
            if (r.body.websocketToken) nextState.websocketToken = r.body.websocketToken;
            if (r.body.accessToken && r.body.sessionToken) {
              nextState.accessToken = r.body.accessToken;
              nextState.sessionToken = r.body.sessionToken;
            }
            storage.state = nextState;
            storage.saveState();
            r.body = { ...r.body };
            delete r.body.websocketToken;
            delete r.body.accessToken;
            delete r.body.sessionToken;
          }
          if (r.body.contentTokens) {
            body = r.body.content;
            Object.keys(r.body.contentTokens).forEach((key) => {
              _tokens[key] = r.body.contentTokens[key];
            });
          } else {
            body = r.body;
          }
          this.stateUpdateCallback(false);
          finish({ body, status: r.statusCode });
        } catch (f) {
          finish({ body: f, status: r.statusCode });
        }
      }
    };

    const { uri, method = 'GET', json, body, headers = {} } = options;
    const fetchHeaders = { ...headers };
    // Only send credentials to Logictry hosts; S3 (DiffCache) returns ACAO: * and would reject include.
    const isLogictryRequest = isLogictryHost(uri);
    const fetchOptions = { method, headers: fetchHeaders };
    if (isLogictryRequest) fetchOptions.credentials = 'include';
    if (body !== undefined) {
      fetchOptions.body = json ? JSON.stringify(body) : body;
      if (json && !fetchHeaders['Content-Type'] && !fetchHeaders['content-type']) {
        fetchHeaders['Content-Type'] = 'application/json';
      }
    }

    fetch(uri, fetchOptions)
      .then(async (response) => {
        const text = await response.text();
        let parsedBody = text;
        if (json) {
          try {
            parsedBody = text ? JSON.parse(text) : null;
          } catch {
            parsedBody = text;
          }
        }
        handleResponse({
          statusCode: response.status,
          body: parsedBody,
          getResponseHeader: (name) => response.headers.get(name),
        });
      })
      .catch((e) => {
        if (e.toString().includes('ERR_INTERNET_DISCONNECTED')) this.stateUpdateCallback(true);
        if (e instanceof SyntaxError) {
          finish({ status: undefined });
        } else {
          finish({ body: e, status: undefined });
        }
      });

    return promise;
  }

  stateUpdateCallback(state) {
    if (state !== _internetDisabled) {
      _internetDisabled = state;
      this.emitStateUpdate();
    }
  }
}

if (Constants.isIphone) {
  // Function to handle online status change
  function handleOnlineStatus() {
    if (navigator.onLine) {
      debugLog('Server', 'online');
    } else {
      debugLog('Server', 'offline');
    }
  }

  // Add event listeners for online and offline events
  window.addEventListener('online', handleOnlineStatus);
  window.addEventListener('offline', handleOnlineStatus);

  // Initial check for online status
  handleOnlineStatus();
}

const singleton = new Server();
export default singleton;
